/**
 * Score coloring shared by the moment cards and the tactical-score bar.
 *
 * This lives in one place on purpose: both surfaces now show the same
 * moment's score, and if they computed thresholds independently the same
 * moment could read teal on the bar and amber in the rail. A viewer who
 * spots that has no way to know which one is lying.
 */
import { ScoreRing } from '../../lib/viz/scoreRing'
import type { Moment } from './types'

/**
 * Whether a moment is served with no score: a null score. The serve layer
 * sends it with a no_score_reason (types.ts). In the moments list and the
 * timeline such a moment shows the label NO_SCORE_LABEL where a score is
 * shown, says noScoreReason on hover, and is drawn in NO_SCORE_COLOR, never
 * on the score colours. An active score filter DROPS it ("above 0.5" is
 * a claim about a score — Ben, 2026-10-26; filtering.passesScoreFilter),
 * and the rail greys its score control when the pick can only ever show
 * unscored moments. A moment whose score is absent is not one of these.
 */
export function hasNoScore(moment: Moment): boolean {
  return moment.score === null
}

/** Where a moment with no score would show its score. */
export const NO_SCORE_LABEL = 'no score'

/** Why, on hover: an empty dashed ring read as a bug (BU@BGSU, 2026-10-23). */
export const NO_SCORE_REASON = 'Not scored: the opponents around this moment '
  + 'were not tracked well enough to measure'

export const RUN_NO_SCORE_REASON = 'Not scored: the players at the end of this run '
  + 'were not tracked well enough to measure'

/** Why a press has no score: the layer stack sends none for a press on a
 *  game with no press moments table (press_not_scored, panels.py). */
export const PRESS_NO_SCORE_REASON = 'Not scored: the press measure is under review'

export const NO_PASS_SCORES_REASON = 'Not scored: pass scores are not built for this game'

export const NO_RUN_SCORES_REASON = 'Not scored: run scores are not built for this game'

export const OUT_OF_RANGE_REASON = 'Not scored: the value is outside the 0 to 100 scale'

/** A chance's xG on a match whose shots are waiting on a person's review
 *  (serve.overlays._xg_review_filter). */
export const XG_WITHHELD_REASON = 'Not scored: xG is shown once this match has been reviewed'

/** A possession window the stack has no peak xD for (panels.py
 *  no_possession_value): a value the model left empty, not opponents the
 *  tracking missed — the tracking text read as a fault that was not there
 *  (SDET release review, 2026-11-03). */
export const NO_POSSESSION_VALUE_REASON = 'Not scored: this possession has no peak xD measured'

const REASON_TEXT: Record<NonNullable<Moment['no_score_reason']>, string> = {
  not_measured: NO_SCORE_REASON,
  no_pass_scores: NO_PASS_SCORES_REASON,
  no_run_scores: NO_RUN_SCORES_REASON,
  out_of_range: OUT_OF_RANGE_REASON,
  press_not_scored: PRESS_NO_SCORE_REASON,
  xg_withheld: XG_WITHHELD_REASON,
  no_possession_value: NO_POSSESSION_VALUE_REASON,
}

const PRESS_TYPES = new Set(['pressing', 'high_press', 'mid_press'])

const RUN_TYPES = new Set(['progressive_run', 'supportive_run'])

/** The hover text for a moment with no score, from its no_score_reason;
 *  a run that is not measured gets RUN_NO_SCORE_REASON. A payload without
 *  one gets the press reason for a press and NO_SCORE_REASON for every
 *  other type. */
export function noScoreReason(moment: Moment): string {
  if (moment.no_score_reason === 'not_measured' && RUN_TYPES.has(moment.type ?? '')) {
    return RUN_NO_SCORE_REASON
  }
  const text = moment.no_score_reason ? REASON_TEXT[moment.no_score_reason] : undefined
  if (text) return text
  return PRESS_TYPES.has(moment.type ?? '') ? PRESS_NO_SCORE_REASON : NO_SCORE_REASON
}

/** What a moment with no score shows inside its ring on the timeline. */
export const NO_SCORE_GLYPH = '\u2013'

/** A moment with no score is drawn in the neutral token (theme/tokens.css),
 *  never in one of the score colours. */
export const NO_SCORE_COLOR = 'var(--state-neutral)'

export interface ScoreThresholds {
  good: number
  okay: number
}

/**
 * Percentile thresholds relative to THIS game's distribution, so roughly a
 * third of moments land in each band (template renderMoments, ~3560). A
 * moment with no score is not in the distribution.
 */
export function dynamicThresholdsFor(moments: Moment[]): ScoreThresholds {
  const scores = moments
    .map((m) => m.score || 0)
    .filter((s) => s > 0)
    .sort((a, b) => a - b)

  if (scores.length >= 3) {
    const p33Index = Math.floor(scores.length * 0.33)
    const p66Index = Math.floor(scores.length * 0.66)
    return {
      good: scores[p66Index] || 0.66,
      okay: scores[p33Index] || 0.33,
    }
  }
  if (scores.length > 0) {
    // With few moments, the median is the only defensible boundary.
    const median = scores[Math.floor(scores.length / 2)]
    if (median !== undefined) return { good: median * 1.1, okay: median * 0.9 }
  }
  return { good: 0.66, okay: 0.33 }
}

/**
 * The teal/amber/coral palette the cards render under
 * `.use-performance-colors`, picked by the same thresholds.
 */
export function performanceColor(score: number, t: ScoreThresholds): string {
  const P = ScoreRing.PERFORMANCE_COLORS
  if (score >= t.good) return P.good
  if (score >= t.okay) return P.okay
  return P.bad
}

/** The cut for the Lab and Analysis Room "score 50+" lists, on the served 0-1 scale. */
export const HIGH_SCORE = 0.5

/** Whether a moment reaches HIGH_SCORE. A moment with no score, or none given, does not. */
export function isHighScore(moment: Moment): boolean {
  return moment.score != null && moment.score >= HIGH_SCORE
}

/** A moment's score times 100, rounded, as the cards print it; NO_SCORE_LABEL for a moment with no score. */
export function scoreText(moment: Moment): string {
  return hasNoScore(moment) ? NO_SCORE_LABEL : String(Math.round((moment.score ?? 0) * 100))
}
